<template>
    <div ref="ticketContainerRef" class="multipleSingleBetSlip">
        <TransitionGroup name="ticketFade">
            <div
                v-for="ticket in multipleSingleTickets"
                v-bind:key="ticket.key"
                v-bind:ref="element => setTicketElement(ticket.key, element)"
                class="ticketContainer"
                v-bind:class="{ _active: isActiveTicket(ticket.key) }"
                v-bind:data-ticket-key="ticket.key"
                tabindex="-1"
            >
                <SingleTicket
                    v-if="ticket.type === TicketType.Single"
                    v-bind:gaClassPrefix="gaClassPrefix"
                    v-bind:ticket="ticket"
                    v-bind:layout="SingleModeTicketLayout.Single"
                    v-on:remove="removeTicket(ticket.key)"
                >
                    <SingleModeTicketControls
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:ticket="ticket"
                        v-bind:isActive="isActiveTicket(ticket.key)"
                    />
                </SingleTicket>
                <DynamicMarketTicket
                    v-else-if="ticket.type === TicketType.DynamicMarket"
                    v-bind:gaClassPrefix="gaClassPrefix"
                    v-bind:ticket="ticket"
                    v-bind:layout="SingleModeTicketLayout.Single"
                    v-on:remove="removeTicket(ticket.key)"
                >
                    <SingleModeTicketControls
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:ticket="ticket"
                        v-bind:isActive="isActiveTicket(ticket.key)"
                    />
                </DynamicMarketTicket>
                <OutrightTicket
                    v-else-if="ticket.type === TicketType.Outright"
                    v-bind:gaClassPrefix="gaClassPrefix"
                    v-bind:ticket="ticket"
                    v-bind:layout="SingleModeTicketLayout.Single"
                    v-on:remove="removeTicket(ticket.key)"
                >
                    <SingleModeTicketControls
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:ticket="ticket"
                        v-bind:isActive="isActiveTicket(ticket.key)"
                    />
                </OutrightTicket>
            </div>
        </TransitionGroup>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import type { ComponentPublicInstance } from 'vue';
    import {
        nextTick,
        onUnmounted,
        ref,
        watch,
    } from 'vue';
    import DynamicMarketTicket from '@/components/betSlip/Single/Ticket/DynamicMarketTicket.vue';
    import OutrightTicket from '@/components/betSlip/Single/Ticket/OutrightTicket.vue';
    import SingleModeTicketControls from '@/components/betSlip/Single/Ticket/SingleModeTicketControls.vue';
    import SingleTicket from '@/components/betSlip/Single/Ticket/SingleTicket.vue';
    import { SingleModeTicketLayout, TicketType } from '@/interface/enum';
    import { useScrollTicketListToBottom } from '@/libraries/composables/useScrollTicketListToBottom';
    import { alignElementBottomWithin, resolveVerticalScrollArea, trackResizeUntilStable } from '@/libraries/utils/scroll';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    const props = withDefaults(defineProps<{
        gaClassPrefix?: string;
        hasFooterError?: boolean;
    }>(), {
        gaClassPrefix: '',
        hasFooterError: false,
    });

    const {
        activeStakeTicketKey,
        isStakePadOpen,
        multipleSingleTickets,
    } = storeToRefs(useSingleModeBetSlipStore());
    const {
        closeStakePad,
        removeTicket,
    } = useSingleModeBetSlipStore();
    const ticketContainerRef = ref<HTMLElement | null>(null);
    const ticketElements = new Map<string, HTMLElement>();
    const ACTIVE_TICKET_BOTTOM_GAP = 48;

    function setTicketElement(
        key: string,
        element: Element | ComponentPublicInstance | null,
    ) {
        if (element instanceof HTMLElement) {
            ticketElements.set(key, element);
            return;
        }
        ticketElements.delete(key);
    }

    function isActiveTicket(key: string): boolean {
        return isStakePadOpen.value && activeStakeTicketKey.value === key;
    }

    useScrollTicketListToBottom(ticketContainerRef);

    function alignActiveTicket(activeTicketKey: string) {
        if (!isStakePadOpen.value || activeStakeTicketKey.value !== activeTicketKey) {
            return;
        }
        const element = ticketElements.get(activeTicketKey);
        if (element === undefined || ticketContainerRef.value === null) {
            return;
        }
        const scrollArea = resolveVerticalScrollArea(ticketContainerRef.value);
        const lastTicket = multipleSingleTickets.value[multipleSingleTickets.value.length - 1];
        if (lastTicket?.key === activeTicketKey) {
            scrollArea.scrollTop = scrollArea.scrollHeight;
            return;
        }
        alignElementBottomWithin(
            scrollArea,
            element,
            props.hasFooterError ? 0 : ACTIVE_TICKET_BOTTOM_GAP,
        );
    }

    watch([activeStakeTicketKey, isStakePadOpen], async ([activeTicketKey, isOpen], [, wasOpen], onCleanup) => {
        let isCancelled = false;
        let stopTrackingResize: (() => void) | null = null;
        onCleanup(() => {
            isCancelled = true;
            stopTrackingResize?.();
        });
        if (!isOpen || activeTicketKey === null) {
            return;
        }
        await nextTick();
        if (
            isCancelled
            || !isStakePadOpen.value
            || activeStakeTicketKey.value !== activeTicketKey
        ) {
            return;
        }
        alignActiveTicket(activeTicketKey);
        if (!wasOpen && ticketContainerRef.value !== null) {
            stopTrackingResize = trackResizeUntilStable(
                ticketContainerRef.value,
                () => alignActiveTicket(activeTicketKey),
            );
        }
    });

    onUnmounted(() => {
        closeStakePad();
        ticketElements.clear();
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/multipleSingleBetSlip";
</style>
